<template>
  <div class="hub-card-wrapper position-relative">
    <div
      class="card h-100 border-0 shadow-sm hub-card bg-white"
      :class="{ 'card-hover-active': isHovered }"
      @mouseenter="isHovered = true"
      @mouseleave="isHovered = false"
      @click="onCardClick"
    >
      <!-- ══════════════════════════════════════════════════════════════════════ -->
      <!-- ── RIGHT-EDGE ATTACHED 3-TAB STRIP (DOCK ACCENT) ──────────────────── -->
      <!-- ══════════════════════════════════════════════════════════════════════ -->
      <div class="hub-card-right-tabs">
        <!-- Tab 1: Regression -->
        <button
          type="button"
          class="hub-edge-tab"
          :class="{ 'active': activeTab === 'regression' }"
          @click.stop="setTab('regression')"
          title="Regression Library &amp; Test Suites"
          aria-label="Regression Library"
        >
          <i class="fa-solid fa-list-check"></i>
          <span class="hub-edge-tab-label">Regression</span>
        </button>

        <!-- Tab 2: Bug Report -->
        <button
          type="button"
          class="hub-edge-tab"
          :class="{ 'active': activeTab === 'bug_report' }"
          @click.stop="setTab('bug_report')"
          title="Bug Reporting Hub &amp; Widget"
          aria-label="Bug Reporting Hub"
        >
          <i class="fa-solid fa-bug"></i>
          <span class="hub-edge-tab-label">Bug Report</span>
        </button>

        <!-- Tab 3: Releases -->
        <button
          type="button"
          class="hub-edge-tab"
          :class="{ 'active': activeTab === 'releases' }"
          @click.stop="setTab('releases')"
          title="Platform Releases &amp; Changelogs"
          aria-label="Platform Releases"
        >
          <i class="fa-solid fa-code-branch"></i>
          <span class="hub-edge-tab-label">Releases</span>
        </button>
      </div>

      <div class="card-body p-4 d-flex flex-column justify-content-between">
        <!-- ── CARD TOP: IDENTITY & ACTIONS ── -->
        <div>
          <!-- Header: Platform Icon + Title/Key + Action Buttons -->
          <div class="d-flex justify-content-between align-items-start gap-2 mb-3">
            <div class="d-flex align-items-center gap-3 min-w-0">
              <div 
                class="hub-platform-icon-wrapper flex-shrink-0"
                :style="iconWrapperStyle"
              >
                <img v-if="isUrl(platform.icon)" :src="platform.icon" class="hub-card-logo" alt="" />
                <i v-else :class="platform.icon || 'fa-solid fa-cubes'"></i>
              </div>
              <div class="min-w-0">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <h4 class="fw-bold text-dark-blue mb-0 hub-card-title text-truncate">{{ platform.name }}</h4>
                  <span class="badge bg-light text-secondary border text-xxs font-monospace px-2 py-1">{{ platform.key }}</span>
                </div>

                <!-- Subtitle Status Line based on activeTab -->
                <div class="d-flex align-items-center gap-2 mt-1 hub-status-line">
                  <template v-if="activeTab === 'regression'">
                    <span class="d-inline-flex align-items-center text-xxs fw-bold text-info">
                      <i class="fa-solid fa-circle me-1" style="font-size: 6px; vertical-align: middle;"></i>
                      <span>Regression Suite</span>
                      <span class="text-muted-custom mx-1">&bull;</span>
                      <span class="text-muted-custom fw-semibold">{{ regressionActiveSprint }}</span>
                    </span>
                  </template>
                  <template v-else-if="activeTab === 'bug_report'">
                    <span 
                      class="d-inline-flex align-items-center text-xxs fw-bold"
                      :class="platform.widget_enabled !== false ? 'text-success' : 'text-secondary'"
                    >
                      <i class="fa-solid fa-circle me-1" style="font-size: 6px; vertical-align: middle;"></i>
                      <span>{{ platform.widget_enabled !== false ? 'Widget Active' : 'Widget Disabled' }}</span>
                      <span class="text-muted-custom mx-1">&bull;</span>
                      <span class="text-muted-custom fw-semibold">Right Edge Tab</span>
                    </span>
                  </template>
                  <template v-else-if="activeTab === 'releases'">
                    <span class="d-inline-flex align-items-center text-xxs fw-bold text-cyan-primary">
                      <i class="fa-solid fa-circle me-1" style="font-size: 6px; vertical-align: middle;"></i>
                      <span>Release Tracking</span>
                      <span class="text-muted-custom mx-1">&bull;</span>
                      <span class="text-muted-custom fw-semibold">{{ releaseLatestVersion }}</span>
                    </span>
                  </template>
                </div>
              </div>
            </div>

            <!-- Header Right: Settings & Delete Buttons -->
            <div class="d-flex align-items-center gap-1 flex-shrink-0">
              <button 
                type="button"
                class="btn btn-sm btn-link text-info border-0 p-1 card-settings-btn" 
                @click.stop="$emit('open-settings', { platform, initialTab: activeTab })" 
                title="Platform Settings"
              >
                <i class="fa-solid fa-gear fs-6"></i>
              </button>
              <button 
                type="button"
                class="btn btn-sm btn-link text-danger border-0 p-1 card-delete-btn" 
                @click.stop="$emit('delete-platform', platform)" 
                title="Delete Platform"
              >
                <i class="fa-solid fa-trash-can fs-6"></i>
              </button>
            </div>
          </div>

          <!-- Platform Description -->
          <p class="text-muted-custom text-xs text-clamp mb-3" style="min-height: 36px; line-height: 1.5;">
            {{ platform.description || 'Unified platform workspace with regression test suites, bug tracking, and release changelogs.' }}
          </p>
        </div>

        <!-- ── CARD MIDDLE: ACTIVE MODULE STATS & METADATA ── -->
        <div>
          <!-- Tabbed Stats Container -->
          <div class="hub-tab-content-container">
            <!-- 1. REGRESSION TAB STATS -->
            <div v-if="activeTab === 'regression'" class="hub-tab-panel animate-fade-in">
              <!-- Master Test Library Progress -->
              <div class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span class="text-muted small fw-semibold">Master Library Tests:</span>
                  <span class="fw-bold text-dark small">{{ regTotalTests }} total</span>
                </div>
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span class="text-muted small fw-semibold">Total Sprints:</span>
                  <span class="fw-bold text-dark small">{{ regTotalSprints }} runs</span>
                </div>
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span class="text-muted small fw-semibold">Sprints Completed:</span>
                  <span class="fw-bold text-success small">{{ regSprintsCompleted }}</span>
                </div>
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span class="text-muted small fw-semibold">Sprints This Year:</span>
                  <span class="fw-bold text-info small">{{ regSprintsThisYear }}</span>
                </div>
              </div>

              <!-- Active Sprint Status Section -->
              <div class="p-3 bg-light rounded-3 mb-3" style="border: 1px dashed #dee2e6;">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <span class="text-muted small fw-bold">Active Sprint:</span>
                  <span
                    v-if="regHasActiveSprint"
                    class="badge bg-success bg-opacity-10 text-success fw-bold px-2 py-1"
                    style="font-size: 11px; border: 1px solid rgba(40,167,69,0.2);"
                  >
                    <i class="fa-solid fa-bolt me-1"></i> {{ regActiveSprintName }}
                  </span>
                  <span v-else class="text-muted small fw-semibold text-secondary" style="font-style: italic;">
                    no active sprint
                  </span>
                </div>

                <div v-if="regHasActiveSprint">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="text-muted small fw-semibold" style="font-size: 11.5px;">Test count:</span>
                    <span class="fw-bold text-dark small" style="font-size: 11.5px;">{{ regSprintTotalTests }} total</span>
                  </div>
                  <div v-if="regSprintMovedTests > 0" class="d-flex justify-content-between align-items-center mb-2" style="margin-top: -3px;">
                    <span class="text-muted" style="font-size: 10.5px;">Breakdown:</span>
                    <span class="text-muted fw-semibold" style="font-size: 10.5px;">
                      {{ regSprintTotalTests - regSprintMovedTests }} active <span class="text-muted mx-2">|</span>
                      <span style="color: #8b5cf6; font-weight: 700;">{{ regSprintMovedTests }} moved</span>
                    </span>
                  </div>
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="text-muted small fw-semibold" style="font-size: 11.5px;">Progress:</span>
                    <span class="fw-bold text-success small" style="font-size: 11.5px;">
                      {{ regSprintProgress }}%
                    </span>
                  </div>
                  <div class="progress" style="height: 6px; border-radius: 3px;">
                    <div
                      class="progress-bar bg-success"
                      role="progressbar"
                      :style="{ width: regSprintProgress + '%' }"
                      :aria-valuenow="regSprintProgress"
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </div>
                <div v-else class="text-center py-2 text-muted" style="font-size: 12px; font-style: italic;">
                  <i class="fa-solid fa-circle-info me-1"></i> Ready to initialize a sprint run
                </div>
              </div>
            </div>

            <!-- 2. BUG REPORT TAB STATS -->
            <div v-else-if="activeTab === 'bug_report'" class="hub-tab-panel animate-fade-in">
              <div class="row g-2 mb-3">
                <div class="col-4">
                  <div class="rounded-3 bg-light border d-flex flex-column align-items-start px-3 py-2">
                    <span class="text-muted-custom text-xxs fw-bold text-uppercase">OPEN</span>
                    <span class="fw-extrabold fs-5 mt-1" :class="bugOpenCount > 0 ? 'text-danger' : 'text-dark-blue'">{{ bugOpenCount }}</span>
                  </div>
                </div>
                <div class="col-4">
                  <div class="rounded-3 bg-light border d-flex flex-column align-items-start px-3 py-2">
                    <span class="text-muted-custom text-xxs fw-bold text-uppercase">RESOLVED</span>
                    <span class="fw-extrabold text-success fs-5 mt-1">{{ bugResolvedCount }}</span>
                  </div>
                </div>
                <div class="col-4">
                  <div class="rounded-3 bg-light border d-flex flex-column align-items-start px-3 py-2">
                    <span class="text-muted-custom text-xxs fw-bold text-uppercase">REPOS</span>
                    <span class="fw-extrabold text-cyan-primary fs-5 mt-1">{{ bugRepoCount }}</span>
                  </div>
                </div>
              </div>

              <!-- Unified 2-Column Metadata Bar -->
              <div class="hub-meta-box mb-3">
                <div class="hub-meta-split">
                  <!-- Left: Jira Board -->
                  <div class="hub-meta-cell">
                    <span class="hub-meta-icon bg-info-subtle text-info">
                      <i class="fa-solid fa-diagram-project"></i>
                    </span>
                    <div class="d-flex flex-column min-w-0">
                      <span class="hub-meta-label">Jira Board</span>
                      <span class="hub-meta-value font-monospace text-truncate">{{ platform.jira_project_key || 'DEV' }}</span>
                    </div>
                  </div>

                  <div class="hub-meta-divider"></div>

                  <!-- Right: Copyable Widget UUID -->
                  <button
                    type="button"
                    class="hub-meta-cell hub-meta-cell--action"
                    @click.stop="copyUuid(platform.widget_uuid)"
                    title="Click to copy Widget UUID"
                  >
                    <span class="hub-meta-icon bg-cyan-subtle text-cyan-primary">
                      <i class="fa-solid fa-fingerprint"></i>
                    </span>
                    <div class="d-flex flex-column align-items-start min-w-0 flex-grow-1">
                      <span class="hub-meta-label">Widget UUID</span>
                      <span class="hub-meta-value font-monospace text-truncate">{{ shortUuid }}</span>
                    </div>
                    <span class="hub-meta-copy-icon" title="Copy UUID">
                      <i class="fa-regular fa-copy"></i>
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <!-- 3. RELEASES TAB STATS -->
            <div v-else-if="activeTab === 'releases'" class="hub-tab-panel animate-fade-in">
              <div class="row g-2 mb-3">
                <div class="col-4">
                  <div class="rounded-3 bg-light border d-flex flex-column align-items-start px-3 py-2">
                    <span class="text-muted-custom text-xxs fw-bold text-uppercase">RELEASES</span>
                    <span class="fw-extrabold text-dark-blue fs-5 mt-1">{{ releaseTotalCount }}</span>
                  </div>
                </div>
                <div class="col-4">
                  <div class="rounded-3 bg-light border d-flex flex-column align-items-start px-3 py-2">
                    <span class="text-muted-custom text-xxs fw-bold text-uppercase">DEPLOYS</span>
                    <span class="fw-extrabold text-success fs-5 mt-1">{{ releaseProdDeploys }}</span>
                  </div>
                </div>
                <div class="col-4">
                  <div class="rounded-3 bg-light border d-flex flex-column align-items-start px-3 py-2">
                    <span class="text-muted-custom text-xxs fw-bold text-uppercase">REPOS</span>
                    <span class="fw-extrabold text-cyan-primary fs-5 mt-1">{{ releaseRepoCount }}</span>
                  </div>
                </div>
              </div>

              <!-- Unified 2-Column Metadata Bar -->
              <div class="hub-meta-box mb-3">
                <div class="hub-meta-split">
                  <!-- Left: Bitbucket Workspace -->
                  <div class="hub-meta-cell min-w-0">
                    <span class="hub-meta-icon bg-primary-subtle text-primary">
                      <i class="fa-brands fa-bitbucket"></i>
                    </span>
                    <div class="d-flex flex-column min-w-0">
                      <span class="hub-meta-label">Workspace</span>
                      <span class="hub-meta-value font-monospace text-truncate">{{ platform.bitbucket_workspace || 'VertexSecurity' }}</span>
                    </div>
                  </div>

                  <div class="hub-meta-divider"></div>

                  <!-- Right: Latest Version -->
                  <div class="hub-meta-cell justify-content-between">
                    <div class="d-flex flex-column min-w-0">
                      <span class="hub-meta-label">Latest Release</span>
                      <span class="hub-meta-value font-monospace text-cyan-primary">{{ releaseLatestVersion }}</span>
                    </div>
                    <span class="version-pill-highlight-sm ms-2">
                      <i class="fa-solid fa-tag opacity-75"></i>
                      <span>{{ releaseLatestVersion }}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── CARD FOOTER: CTA BUTTON ── -->
          <div class="d-flex align-items-center justify-content-between pt-2 border-top border-light text-cyan-primary fw-bold text-xs card-action-link">
            <span>{{ footerCtaText }}</span>
            <i class="fa-solid fa-arrow-right-long card-arrow"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "HubCard",
  props: {
    platform: {
      type: Object,
      required: true
    },
    initialTab: {
      type: String,
      default: "regression"
    }
  },
  emits: ["open-platform", "open-settings", "delete-platform", "copy-uuid"],
  data() {
    return {
      activeTab: this.initialTab || "regression",
      isHovered: false
    };
  },
  watch: {
    initialTab(newVal) {
      if (newVal) {
        this.activeTab = newVal;
      }
    }
  },
  computed: {
    iconWrapperStyle() {
      const color = this.platform.primary_color || "#00a0c1";
      const isCustomUrl = this.isUrl(this.platform.icon);
      return {
        backgroundColor: isCustomUrl ? "#ffffff" : this.hexToRgba(color, 0.12),
        color: color,
        border: isCustomUrl ? "1px solid #e2e8f0" : `1px solid ${this.hexToRgba(color, 0.28)}`
      };
    },
    shortUuid() {
      if (!this.platform.widget_uuid) return "UUID";
      return `${this.platform.widget_uuid.slice(0, 8)}…`;
    },
    stats() {
      return this.platform.hub_stats || {};
    },
    // Regression stats
    regTotalTests() {
      return this.stats.regression?.total_tests ?? this.stats.regression?.tests ?? this.platform.total_tests ?? 0;
    },
    regTotalSprints() {
      return this.stats.regression?.total_sprints ?? this.platform.total_sprints ?? 0;
    },
    regSprintsCompleted() {
      return this.stats.regression?.sprints_completed ?? this.platform.sprints_completed ?? 0;
    },
    regSprintsThisYear() {
      return this.stats.regression?.sprints_this_year ?? this.platform.sprints_this_year ?? 0;
    },
    regHasActiveSprint() {
      return Boolean(
        this.stats.regression?.has_active_sprint ??
        this.platform.has_active_sprint ??
        (this.stats.regression?.active_sprint && this.stats.regression?.active_sprint !== "no active sprint")
      );
    },
    regActiveSprintName() {
      return (
        this.stats.regression?.active_sprint_name ||
        this.stats.regression?.active_sprint ||
        this.platform.active_sprint_name ||
        "no active sprint"
      );
    },
    regSprintTotalTests() {
      return this.stats.regression?.sprint_total_tests ?? this.platform.sprint_total_tests ?? 0;
    },
    regSprintPassedTests() {
      return this.stats.regression?.sprint_passed_tests ?? this.platform.sprint_passed_tests ?? 0;
    },
    regSprintMovedTests() {
      return this.stats.regression?.sprint_moved_tests ?? this.platform.sprint_moved_tests ?? 0;
    },
    regSprintProgress() {
      const active = this.regSprintTotalTests - this.regSprintMovedTests;
      if (active > 0) {
        return Math.min(100, Math.max(0, Math.round((this.regSprintPassedTests / active) * 100)));
      }
      if (this.stats.regression?.sprint_progress !== undefined) {
        return Math.min(100, Math.max(0, Math.round(this.stats.regression.sprint_progress)));
      }
      return 0;
    },
    regressionActiveSprint() {
      return this.regActiveSprintName;
    },

    // Bug report stats
    bugOpenCount() {
      return this.platform.hub_stats?.bug_report?.open ?? this.platform.open_bugs_count ?? this.platform.open_bugs ?? 0;
    },
    bugResolvedCount() {
      return this.platform.hub_stats?.bug_report?.resolved ?? this.platform.resolved_bugs_count ?? this.platform.resolved_bugs ?? 0;
    },
    bugRepoCount() {
      return this.platform.hub_stats?.bug_report?.repos ?? (this.platform.repositories ? this.platform.repositories.length : 0);
    },

    // Release stats
    releaseTotalCount() {
      return this.platform.hub_stats?.releases?.total ?? this.platform.total_releases ?? 0;
    },
    releaseProdDeploys() {
      return this.platform.hub_stats?.releases?.prod_deploys ?? this.platform.prod_deploys ?? this.releaseTotalCount;
    },
    releaseRepoCount() {
      return this.platform.hub_stats?.releases?.repos ?? (this.platform.repositories ? this.platform.repositories.length : 0);
    },
    releaseLatestVersion() {
      return this.platform.hub_stats?.releases?.latest_version || this.platform.latest_version || "v1.0.0";
    },

    // Dynamic CTA footer text
    footerCtaText() {
      switch (this.activeTab) {
        case "regression":
          return "Open Regression Suite";
        case "bug_report":
          return "Open Bug Reporting Hub";
        case "releases":
          return "Open Platform Releases";
        default:
          return "Open Platform Workspace";
      }
    }
  },
  methods: {
    setTab(tab) {
      this.activeTab = tab;
    },
    onCardClick() {
      this.$emit("open-platform", {
        platform: this.platform,
        module: this.activeTab
      });
    },
    isUrl(val) {
      return typeof val === "string" && (val.startsWith("http://") || val.startsWith("https://") || val.startsWith("/"));
    },
    hexToRgba(hex, alpha) {
      if (!hex) return `rgba(0, 160, 193, ${alpha})`;
      const clean = hex.replace("#", "");
      let r, g, b;
      if (clean.length === 3) {
        r = parseInt(clean[0] + clean[0], 16);
        g = parseInt(clean[1] + clean[1], 16);
        b = parseInt(clean[2] + clean[2], 16);
      } else if (clean.length === 6) {
        r = parseInt(clean.substring(0, 2), 16);
        g = parseInt(clean.substring(2, 4), 16);
        b = parseInt(clean.substring(4, 6), 16);
      } else {
        return `rgba(0, 160, 193, ${alpha})`;
      }
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    async copyUuid(uuid) {
      if (!uuid) return;
      this.$emit("copy-uuid", uuid);
      try {
        await navigator.clipboard.writeText(uuid);
        if (this.$toast) {
          this.$toast.success("Widget UUID copied to clipboard!");
        }
      } catch (e) {
        console.error(e);
      }
    }
  }
};
</script>

<style scoped>
/* ── WRAPPER & CARD CONTAINER ── */
.hub-card-wrapper {
  padding-right: 38px; /* Reserves dedicated space for right-edge attached tabs */
  height: 100%;
}

.hub-card {
  position: relative;
  min-height: 285px;
  border-radius: 12px !important;
  cursor: pointer;
  transition: transform 0.22s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.22s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.22s ease;
  border: 1px solid #e2e8f0 !important;
}

.hub-card:hover,
.hub-card.card-hover-active {
  transform: translateY(-4px);
  box-shadow: 0 14px 30px -6px rgba(15, 23, 42, 0.12), 0 4px 14px -2px rgba(0, 160, 193, 0.09) !important;
  border-color: rgba(0, 160, 193, 0.4) !important;
}

/* ── RIGHT-EDGE ATTACHED 3-TAB STRIP ── */
.hub-card-right-tabs {
  position: absolute;
  left: calc(100% - 1px);
  top: 16px;
  bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 5;
}

.hub-edge-tab {
  width: 32px;
  flex: 1 1 0;
  min-height: 68px;
  border-radius: 0 8px 8px 0;
  border: 1px solid #cbd5e1;
  border-left: none !important;
  background: #f8fafc;
  color: #64748b;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 4px;
  cursor: pointer;
  transition: width 0.18s ease, background-color 0.18s ease, color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
  box-shadow: 2px 2px 5px rgba(15, 23, 42, 0.04);
  margin-left: 0;
  outline: none;
}

.hub-edge-tab:hover {
  width: 35px;
  background: #f0f9ff;
  color: #0891b2;
  border-color: #7dd3fc;
}

.hub-edge-tab.active {
  width: 36px;
  background: linear-gradient(180deg, #00a0c1 0%, #0891b2 100%);
  color: #ffffff;
  border-color: #0891b2;
  box-shadow: 3px 2px 8px rgba(8, 145, 178, 0.22);
  z-index: 6;
}

.hub-edge-tab i {
  font-size: 12px;
}

.hub-edge-tab-label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.2px;
}

.btn-xs {
  font-size: 11px;
  padding: 3px 8px;
  line-height: 1.3;
}

.fw-extrabold {
  font-weight: 800 !important;
}

.version-pill-highlight-sm {
  background: linear-gradient(135deg, #00a0c1 0%, #00738c 100%) !important;
  color: #ffffff !important;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  padding: 3px 10px !important;
  border-radius: 12px !important;
  letter-spacing: 0.3px !important;
  box-shadow: 0 1px 4px rgba(0, 160, 193, 0.3) !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 6px !important;
  line-height: 1.3 !important;
}

/* ── UNIFIED METADATA BOX & 2-COLUMN SPLIT ── */
.hub-meta-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 9px 12px;
}

.hub-meta-split {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.hub-meta-cell {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  padding: 2px 4px;
  border-radius: 6px;
  text-align: left;
}

.hub-meta-cell--action {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.hub-meta-cell--action:hover {
  background-color: #eef6f8;
}

.hub-meta-cell--action:hover .hub-meta-copy-icon {
  color: #00a0c1;
  border-color: #a5f3fc;
  background: #ffffff;
}

.hub-meta-divider {
  width: 1px;
  height: 26px;
  background-color: #e2e8f0;
  flex-shrink: 0;
}

.hub-meta-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
}

.hub-meta-label {
  font-size: 9.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #64748b;
  line-height: 1.1;
}

.hub-meta-value {
  font-size: 11.5px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.25;
  margin-top: 1px;
}

.hub-meta-copy-icon {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
  transition: all 0.15s ease;
}

/* ── CARD HEADER & LOGO ── */
.hub-platform-icon-wrapper {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.hub-card-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.hub-card-title {
  font-size: 1.05rem;
  letter-spacing: -0.2px;
}

.hub-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.card-settings-btn:hover {
  transform: rotate(45deg);
  transition: transform 0.2s ease;
}

.card-delete-btn:hover {
  transform: scale(1.1);
  transition: transform 0.2s ease;
}

/* ── ANIMATION & HOVER EFFECTS ── */
.animate-fade-in {
  animation: hubFadeIn 0.2s ease-out;
}

@keyframes hubFadeIn {
  from {
    opacity: 0;
    transform: translateY(3px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hub-card:hover .card-action-link {
  color: #0891b2 !important;
}

.hub-card:hover .card-arrow {
  transform: translateX(4px);
  transition: transform 0.2s ease;
}

.card-arrow {
  transition: transform 0.2s ease;
}

.text-dark-blue {
  color: #0f172a !important;
}

.text-muted-custom {
  color: #64748b !important;
}

.text-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.text-xxs {
  font-size: 0.6875rem !important;
}

.text-cyan-primary {
  color: #00A0C1 !important;
}

.bg-cyan-primary {
  background-color: #00A0C1 !important;
}

.bg-cyan-subtle {
  background-color: #ecfeff !important;
}

.text-cyan {
  color: #0891b2 !important;
}

.border-cyan-subtle {
  border-color: #a5f3fc !important;
}
</style>
